import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useFloatMessage, MESSAGE_TYPE } from '../../../Providers/FloatMessage';
import * as DL from '../../../../utilities/DL/Cronometer';

/**
 * Modal Administrativo de Gestión de Temas Visuales y Patrocinadores para Cronómetro (Tile 50).
 * Permite listar temas existentes, activarlos en tiempo real para el estadio y celulares,
 * crear nuevos temas, editarlos, seleccionar QR prediseñado (/img/games/cronometer/qr-cronometro.png)
 * o subir assets optimizados en WebP mediante SkiaSharp en el backend.
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.isOpen - Si el modal está visible.
 * @param {Function} props.onClose - Callback al cerrar el modal.
 * @param {Function} [props.onThemeActivated] - Callback cuando un tema se activa exitosamente.
 */
export function CronometerThemeModal({ isOpen, onClose, onThemeActivated }) {
    const { showMessage } = useFloatMessage();

    // Vistas: 'list' (catálogo) | 'form' (crear/editar)
    const [currentView, setCurrentView] = useState('list');
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadingField, setUploadingField] = useState(null);

    // Lista de temas
    const [themesList, setThemesList] = useState([]);
    const [activeThemeId, setActiveThemeId] = useState(null);

    // Formulario de Tema
    const initialFormState = {
        id: null,
        name: '',
        coverTitle: 'DETÉN EL CRONÓMETRO',
        coverTextColor: '#FFFFFF',
        coverBackgroundUrl: '',
        gameBackgroundUrl: '',
        gameAccentColor: '#E30613',
        fanBackgroundUrl: '',
        fanBackgroundColor: '#001A36',
        fanPrimaryColor: '#E30613',
        brandLogoUrl: '',
        sponsorLogoUrl: '',
        qrSourceType: 'preset', // 'preset' | 'custom'
        qrImageUrl: '/img/games/cronometer/qr-cronometro.png'
    };

    const [formData, setFormData] = useState(initialFormState);
    const fileInputRefs = {
        brandLogo: useRef(null),
        sponsorLogo: useRef(null),
        coverBackground: useRef(null),
        gameBackground: useRef(null),
        fanBackground: useRef(null),
        customQr: useRef(null)
    };

    const loadThemes = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await DL.GetThemes();
            if (response && response.success && Array.isArray(response.data)) {
                setThemesList(response.data);
                const active = response.data.find(t => t.isActive);
                if (active) {
                    setActiveThemeId(active.id);
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response?.message || 'Error al obtener la lista de temas.'
                });
            }
        } catch (error) {
            console.error('Error al cargar temas de Cronómetro:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al cargar temas.'
            });
        } finally {
            setIsLoading(false);
        }
    }, [showMessage]);

    // Cargar temas al abrir el modal
    useEffect(() => {
        if (isOpen) {
            loadThemes();
            setCurrentView('list');
        }
    }, [isOpen, loadThemes]);

    const handleOpenCreateForm = () => {
        setFormData({
            ...initialFormState,
            name: `Tema Jornada ${themesList.length + 1}`
        });
        setCurrentView('form');
    };

    const handleOpenEditForm = (theme) => {
        const isPresetQr = !theme.qrImageUrl || theme.qrImageUrl.includes('qr-cronometro.png');
        setFormData({
            id: theme.id,
            name: theme.name || '',
            coverTitle: theme.coverTitle || 'DETÉN EL CRONÓMETRO',
            coverTextColor: theme.coverTextColor || '#FFFFFF',
            coverBackgroundUrl: theme.coverBackgroundUrl || '',
            gameBackgroundUrl: theme.gameBackgroundUrl || '',
            gameAccentColor: theme.gameAccentColor || '#E30613',
            fanBackgroundUrl: theme.fanBackgroundUrl || '',
            fanBackgroundColor: theme.fanBackgroundColor || '#001A36',
            fanPrimaryColor: theme.fanPrimaryColor || '#E30613',
            brandLogoUrl: theme.brandLogoUrl || '',
            sponsorLogoUrl: theme.sponsorLogoUrl || '',
            qrSourceType: isPresetQr ? 'preset' : 'custom',
            qrImageUrl: theme.qrImageUrl || '/img/games/cronometer/qr-cronometro.png'
        });
        setCurrentView('form');
    };

    const handleActivateTheme = async (themeId) => {
        try {
            setIsLoading(true);
            const response = await DL.SetActiveTheme(themeId);
            if (response && response.success) {
                setActiveThemeId(themeId);
                setThemesList(prev => prev.map(t => ({
                    ...t,
                    isActive: t.id === themeId
                })));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: '¡Tema activado exitosamente para el estadio y la afición!'
                });
                if (onThemeActivated && typeof onThemeActivated === 'function') {
                    onThemeActivated(themeId);
                }
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response?.message || 'Error al activar el tema.'
                });
            }
        } catch (error) {
            console.error('Error al activar tema:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al activar el tema.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteTheme = async (themeId) => {
        if (!window.confirm('¿Estás seguro de que deseas eliminar este tema?')) return;
        try {
            setIsLoading(true);
            const response = await DL.DeleteTheme(themeId);
            if (response && response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Tema eliminado correctamente.'
                });
                loadThemes();
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response?.message || 'No se puede eliminar un tema activo.'
                });
            }
        } catch (error) {
            console.error('Error al eliminar tema:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al eliminar el tema.'
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleQrTypeChange = (type) => {
        if (type === 'preset') {
            setFormData(prev => ({
                ...prev,
                qrSourceType: 'preset',
                qrImageUrl: '/img/games/cronometer/qr-cronometro.png'
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                qrSourceType: 'custom',
                qrImageUrl: prev.qrImageUrl.includes('qr-cronometro.png') ? '' : prev.qrImageUrl
            }));
        }
    };

    const handleFileUpload = async (fieldName, file) => {
        if (!file) return;

        // Validar tipo de archivo
        const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
        if (!validTypes.includes(file.type)) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Formato no soportado. Usa PNG, JPG, WebP o SVG.'
            });
            return;
        }

        try {
            setUploadingField(fieldName);
            showMessage({
                type: MESSAGE_TYPE.INFO,
                text: 'Optimizando imagen con SkiaSharp...'
            });

            const uploadRes = await DL.UploadThemeAsset(file);
            if (uploadRes && uploadRes.success && uploadRes.url) {
                setFormData(prev => ({
                    ...prev,
                    [fieldName]: uploadRes.url
                }));
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: 'Imagen optimizada a WebP exitosamente.'
                });
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: uploadRes?.message || 'Error al procesar la imagen.'
                });
            }
        } catch (error) {
            console.error('Error al subir asset:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error de conexión al subir la imagen.'
            });
        } finally {
            setUploadingField(null);
        }
    };

    const handleSaveTheme = async (e) => {
        if (e && e.preventDefault) e.preventDefault();

        if (!formData.name || !formData.name.trim()) {
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'El nombre del tema es obligatorio.'
            });
            return;
        }

        try {
            setIsSaving(true);
            const payload = {
                name: formData.name.trim(),
                coverTitle: formData.coverTitle?.trim() || 'DETÉN EL CRONÓMETRO',
                coverTextColor: formData.coverTextColor || '#FFFFFF',
                coverBackgroundUrl: formData.coverBackgroundUrl || null,
                gameBackgroundUrl: formData.gameBackgroundUrl || null,
                gameAccentColor: formData.gameAccentColor || '#E30613',
                fanBackgroundUrl: formData.fanBackgroundUrl || null,
                fanBackgroundColor: formData.fanBackgroundColor || '#001A36',
                fanPrimaryColor: formData.fanPrimaryColor || '#E30613',
                brandLogoUrl: formData.brandLogoUrl || null,
                sponsorLogoUrl: formData.sponsorLogoUrl || null,
                qrImageUrl: formData.qrImageUrl || '/img/games/cronometer/qr-cronometro.png'
            };

            let response;
            if (formData.id) {
                response = await DL.UpdateTheme(formData.id, payload);
            } else {
                response = await DL.CreateTheme(payload);
            }

            if (response && response.success) {
                showMessage({
                    type: MESSAGE_TYPE.SUCCESS,
                    text: formData.id ? '¡Tema actualizado exitosamente!' : '¡Tema creado exitosamente!'
                });
                await loadThemes();
                setCurrentView('list');
            } else {
                showMessage({
                    type: MESSAGE_TYPE.BAD,
                    text: response?.message || 'Error al guardar el tema visual.'
                });
            }
        } catch (error) {
            console.error('Error al guardar tema:', error);
            showMessage({
                type: MESSAGE_TYPE.BAD,
                text: 'Error inesperado al guardar el tema.'
            });
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div
            className="modal fade show d-block crono-modal-overlay"
            tabIndex="-1"
            style={{
                backgroundColor: 'rgba(10, 25, 47, 0.75)',
                backdropFilter: 'blur(6px)',
                zIndex: 1060
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget && !isSaving && !uploadingField) {
                    onClose();
                }
            }}
        >
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content shadow-lg border-0 rounded-4 overflow-hidden bg-white text-dark">
                    {/* Header */}
                    <div className="modal-header border-bottom px-4 py-3 bg-white d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center gap-3">
                            <div
                                className="bg-danger text-white rounded-3 p-2 d-flex align-items-center justify-content-center"
                                style={{ width: '42px', height: '42px' }}
                            >
                                <i className="bi bi-palette-fill fs-5"></i>
                            </div>
                            <div>
                                <h5 className="modal-title fw-bold text-dark mb-0">Gestión de Temas y Patrocinadores</h5>
                                <span className="small text-muted">Personalización visual de Pantalla Estadio y Mobile Fan</span>
                            </div>
                        </div>

                        <div className="d-flex align-items-center gap-2">
                            {currentView === 'form' && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary fw-semibold"
                                    onClick={() => setCurrentView('list')}
                                    disabled={isSaving || Boolean(uploadingField)}
                                >
                                    <i className="bi bi-arrow-left me-1"></i> Volver al listado
                                </button>
                            )}
                            {currentView === 'list' && (
                                <button
                                    type="button"
                                    className="btn btn-sm btn-danger fw-bold shadow-sm"
                                    onClick={handleOpenCreateForm}
                                    disabled={isLoading}
                                >
                                    <i className="bi bi-plus-circle me-1"></i> Crear Nuevo Tema
                                </button>
                            )}
                            <button
                                type="button"
                                className="btn-close"
                                aria-label="Close"
                                disabled={isSaving || Boolean(uploadingField)}
                                onClick={onClose}
                            ></button>
                        </div>
                    </div>

                    {/* Body */}
                    <div className="modal-body p-4 bg-light">
                        {/* ============================================================== */}
                        {/* VISTA 1: LISTADO DE TEMAS                                      */}
                        {/* ============================================================== */}
                        {currentView === 'list' && (
                            <div>
                                {isLoading ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-danger mb-3" role="status"></div>
                                        <p className="text-muted fw-semibold">Cargando catálogo de temas...</p>
                                    </div>
                                ) : themesList.length === 0 ? (
                                    <div className="text-center py-5 bg-white rounded-4 shadow-sm p-4">
                                        <i className="bi bi-brush fs-1 text-muted mb-3 d-block"></i>
                                        <h5 className="fw-bold text-dark">No hay temas registrados</h5>
                                        <p className="text-muted small mb-4">
                                            Crea el primer tema visual para personalizar logos, colores y fondos de la dinámica.
                                        </p>
                                        <button
                                            type="button"
                                            className="btn btn-danger fw-bold px-4 py-2"
                                            onClick={handleOpenCreateForm}
                                        >
                                            <i className="bi bi-plus-lg me-1"></i> Crear Primer Tema
                                        </button>
                                    </div>
                                ) : (
                                    <div className="row g-3">
                                        {themesList.map((theme) => {
                                            const isActive = theme.id === activeThemeId || theme.isActive;
                                            return (
                                                <div key={theme.id} className="col-12 col-md-6 col-lg-4">
                                                    <div
                                                        className={`card h-100 border-2 shadow-sm rounded-4 overflow-hidden position-relative transition ${
                                                            isActive ? 'border-success' : 'border-light'
                                                        }`}
                                                        style={{ background: '#ffffff' }}
                                                    >
                                                        {/* Badge de Activo */}
                                                        {isActive && (
                                                            <div className="position-absolute top-0 end-0 m-3 z-1">
                                                                <span className="badge bg-success text-white px-2.5 py-1.5 fw-bold shadow-sm d-flex align-items-center gap-1">
                                                                    <i className="bi bi-check-circle-fill"></i> ACTIVO EN VIVO
                                                                </span>
                                                            </div>
                                                        )}

                                                        {/* Vista Previa Header */}
                                                        <div
                                                            className="p-3 text-white text-center position-relative"
                                                            style={{
                                                                backgroundColor: theme.fanBackgroundColor || '#001A36',
                                                                minHeight: '110px',
                                                                backgroundImage: theme.coverBackgroundUrl ? `url(${theme.coverBackgroundUrl})` : 'none',
                                                                backgroundSize: 'cover',
                                                                backgroundPosition: 'center'
                                                            }}
                                                        >
                                                            <div
                                                                className="position-absolute top-0 start-0 w-100 h-100"
                                                                style={{ backgroundColor: 'rgba(0,0,0,0.4)' }}
                                                            ></div>

                                                            <div className="position-relative z-1 d-flex flex-column align-items-center justify-content-center h-100">
                                                                {theme.brandLogoUrl && (
                                                                    <img
                                                                        src={theme.brandLogoUrl}
                                                                        alt="Brand"
                                                                        style={{ maxHeight: '36px', maxWidth: '120px', objectFit: 'contain' }}
                                                                        className="mb-1"
                                                                    />
                                                                )}
                                                                <span
                                                                    className="fw-bold small text-uppercase"
                                                                    style={{ color: theme.coverTextColor || '#FFFFFF' }}
                                                                >
                                                                    {theme.coverTitle || 'DETÉN EL CRONÓMETRO'}
                                                                </span>
                                                            </div>
                                                        </div>

                                                        {/* Detalles y Paleta */}
                                                        <div className="card-body p-3 d-flex flex-column justify-content-between">
                                                            <div>
                                                                <h6 className="fw-bold text-dark mb-1">{theme.name}</h6>
                                                                <div className="d-flex align-items-center gap-2 mb-3">
                                                                    <span className="small text-muted">Paleta:</span>
                                                                    <span
                                                                        className="rounded-circle d-inline-block border"
                                                                        style={{ width: '16px', height: '16px', backgroundColor: theme.gameAccentColor || '#E30613' }}
                                                                        title="Game Accent"
                                                                    ></span>
                                                                    <span
                                                                        className="rounded-circle d-inline-block border"
                                                                        style={{ width: '16px', height: '16px', backgroundColor: theme.fanBackgroundColor || '#001A36' }}
                                                                        title="Fan Background"
                                                                    ></span>
                                                                    <span
                                                                        className="rounded-circle d-inline-block border"
                                                                        style={{ width: '16px', height: '16px', backgroundColor: theme.fanPrimaryColor || '#E30613' }}
                                                                        title="Fan Primary"
                                                                    ></span>
                                                                </div>

                                                                {/* Indicadores de assets configurados */}
                                                                <div className="d-flex flex-wrap gap-1 mb-3">
                                                                    {theme.sponsorLogoUrl && (
                                                                        <span className="badge bg-light text-secondary border small">
                                                                            <i className="bi bi-star-fill text-warning me-1"></i> Sponsor
                                                                        </span>
                                                                    )}
                                                                    {theme.gameBackgroundUrl && (
                                                                        <span className="badge bg-light text-secondary border small">
                                                                            <i className="bi bi-image me-1"></i> Fondo Juego
                                                                        </span>
                                                                    )}
                                                                    {theme.qrImageUrl && (
                                                                        <span className="badge bg-light text-secondary border small">
                                                                            <i className="bi bi-qr-code me-1"></i> QR
                                                                        </span>
                                                                    )}
                                                                </div>
                                                            </div>

                                                            {/* Acciones */}
                                                            <div className="d-flex align-items-center justify-content-between gap-2 pt-2 border-top">
                                                                {!isActive ? (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-success fw-bold flex-grow-1"
                                                                        onClick={() => handleActivateTheme(theme.id)}
                                                                        disabled={isLoading}
                                                                    >
                                                                        <i className="bi bi-broadcast me-1"></i> Activar
                                                                    </button>
                                                                ) : (
                                                                    <span className="text-success small fw-bold flex-grow-1 text-center py-1">
                                                                        <i className="bi bi-check2-all"></i> En Transmisión
                                                                    </span>
                                                                )}

                                                                <button
                                                                    type="button"
                                                                    className="btn btn-sm btn-outline-primary"
                                                                    title="Editar tema"
                                                                    onClick={() => handleOpenEditForm(theme)}
                                                                    disabled={isLoading}
                                                                >
                                                                    <i className="bi bi-pencil-fill"></i>
                                                                </button>

                                                                {!isActive && (
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        title="Eliminar tema"
                                                                        onClick={() => handleDeleteTheme(theme.id)}
                                                                        disabled={isLoading}
                                                                    >
                                                                        <i className="bi bi-trash-fill"></i>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ============================================================== */}
                        {/* VISTA 2: FORMULARIO CREAR / EDITAR TEMA                        */}
                        {/* ============================================================== */}
                        {currentView === 'form' && (
                            <form onSubmit={handleSaveTheme}>
                                <div className="row g-4">
                                    {/* Columna Izquierda: Parámetros y Colores */}
                                    <div className="col-12 col-lg-7">
                                        {/* Card 1: Identificación */}
                                        <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-3">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <i className="bi bi-info-circle-fill text-danger"></i>
                                                Información General del Tema
                                            </h6>

                                            <div className="row g-3">
                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                                        Nombre del Tema <span className="text-danger">*</span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="name"
                                                        placeholder="Ej: Jornada 14 - Canel's"
                                                        value={formData.name}
                                                        onChange={handleInputChange}
                                                        required
                                                    />
                                                </div>

                                                <div className="col-12 col-md-6">
                                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                                        Título Portada Pantalla
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="form-control"
                                                        name="coverTitle"
                                                        placeholder="DETÉN EL CRONÓMETRO"
                                                        value={formData.coverTitle}
                                                        onChange={handleInputChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card 2: Paleta de Colores */}
                                        <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-3">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <i className="bi bi-palette-fill text-danger"></i>
                                                Paleta Cromática Oficial
                                            </h6>

                                            <div className="row g-3">
                                                <div className="col-12 col-md-4">
                                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                                        Color Acento Pantalla
                                                    </label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color p-1 rounded-3"
                                                            name="gameAccentColor"
                                                            value={formData.gameAccentColor}
                                                            onChange={handleInputChange}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase fw-semibold"
                                                            name="gameAccentColor"
                                                            value={formData.gameAccentColor}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-4">
                                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                                        Fondo Aficionado (Mobile)
                                                    </label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color p-1 rounded-3"
                                                            name="fanBackgroundColor"
                                                            value={formData.fanBackgroundColor}
                                                            onChange={handleInputChange}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase fw-semibold"
                                                            name="fanBackgroundColor"
                                                            value={formData.fanBackgroundColor}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>

                                                <div className="col-12 col-md-4">
                                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                                        Primario Aficionado
                                                    </label>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <input
                                                            type="color"
                                                            className="form-control form-control-color p-1 rounded-3"
                                                            name="fanPrimaryColor"
                                                            value={formData.fanPrimaryColor}
                                                            onChange={handleInputChange}
                                                        />
                                                        <input
                                                            type="text"
                                                            className="form-control form-control-sm text-uppercase fw-semibold"
                                                            name="fanPrimaryColor"
                                                            value={formData.fanPrimaryColor}
                                                            onChange={handleInputChange}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Card 3: Selección de Código QR */}
                                        <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
                                            <h6 className="fw-bold text-dark mb-2 d-flex align-items-center gap-2">
                                                <i className="bi bi-qr-code-scan text-danger"></i>
                                                Código QR para Pantalla de Estadio
                                            </h6>
                                            <p className="small text-muted mb-3">
                                                Elige el QR prediseñado oficial del club o sube un arte patrocinado especial.
                                            </p>

                                            <div className="d-flex gap-3 mb-3">
                                                <div
                                                    className={`p-3 rounded-3 border-2 flex-grow-1 cursor-pointer transition ${
                                                        formData.qrSourceType === 'preset'
                                                            ? 'border-danger bg-danger-subtle text-danger fw-bold'
                                                            : 'border-light bg-light text-secondary'
                                                    }`}
                                                    onClick={() => handleQrTypeChange('preset')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-qr-code fs-4"></i>
                                                        <div>
                                                            <div className="small">QR Oficial Prediseñado</div>
                                                            <span className="x-small text-muted">/img/games/cronometer/qr-cronometro.png</span>
                                                        </div>
                                                    </div>
                                                </div>

                                                <div
                                                    className={`p-3 rounded-3 border-2 flex-grow-1 cursor-pointer transition ${
                                                        formData.qrSourceType === 'custom'
                                                            ? 'border-danger bg-danger-subtle text-danger fw-bold'
                                                            : 'border-light bg-light text-secondary'
                                                    }`}
                                                    onClick={() => handleQrTypeChange('custom')}
                                                    style={{ cursor: 'pointer' }}
                                                >
                                                    <div className="d-flex align-items-center gap-2">
                                                        <i className="bi bi-cloud-arrow-up fs-4"></i>
                                                        <div>
                                                            <div className="small">QR Personalizado / Patrocinado</div>
                                                            <span className="x-small text-muted">Subir imagen propia</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>

                                            {formData.qrSourceType === 'custom' && (
                                                <div>
                                                    <label className="form-label small fw-semibold text-secondary mb-1">
                                                        Archivo de QR Personalizado (PNG o WebP)
                                                    </label>
                                                    <div className="input-group">
                                                        <input
                                                            type="file"
                                                            ref={fileInputRefs.customQr}
                                                            className="form-control"
                                                            accept="image/*"
                                                            onChange={(e) => handleFileUpload('qrImageUrl', e.target.files[0])}
                                                            disabled={uploadingField === 'qrImageUrl'}
                                                        />
                                                        {uploadingField === 'qrImageUrl' && (
                                                            <span className="input-group-text bg-white">
                                                                <div className="spinner-border spinner-border-sm text-danger" role="status"></div>
                                                            </span>
                                                        )}
                                                    </div>
                                                    {formData.qrImageUrl && (
                                                        <div className="mt-2 text-muted small d-flex align-items-center gap-2">
                                                            <i className="bi bi-check-circle-fill text-success"></i>
                                                            <span>Ruta actual: <code>{formData.qrImageUrl}</code></span>
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    {/* Columna Derecha: Subida de Logos y Fondos */}
                                    <div className="col-12 col-lg-5">
                                        <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-3">
                                            <h6 className="fw-bold text-dark mb-3 d-flex align-items-center gap-2">
                                                <i className="bi bi-image text-danger"></i>
                                                Logos y Fondos Gráficos
                                            </h6>

                                            {/* Brand Logo */}
                                            <div className="mb-3">
                                                <label className="form-label small fw-semibold text-secondary mb-1 d-flex justify-content-between">
                                                    <span>Logo de la Marca / Club</span>
                                                    {formData.brandLogoUrl && <span className="text-success small"><i className="bi bi-check"></i> Subido</span>}
                                                </label>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.brandLogo}
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload('brandLogoUrl', e.target.files[0])}
                                                        disabled={uploadingField === 'brandLogoUrl'}
                                                    />
                                                </div>
                                            </div>

                                            {/* Sponsor Logo */}
                                            <div className="mb-3">
                                                <label className="form-label small fw-semibold text-secondary mb-1 d-flex justify-content-between">
                                                    <span>Logo del Patrocinador Oficial</span>
                                                    {formData.sponsorLogoUrl && <span className="text-success small"><i className="bi bi-check"></i> Subido</span>}
                                                </label>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.sponsorLogo}
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload('sponsorLogoUrl', e.target.files[0])}
                                                        disabled={uploadingField === 'sponsorLogoUrl'}
                                                    />
                                                </div>
                                            </div>

                                            {/* Cover Background */}
                                            <div className="mb-3">
                                                <label className="form-label small fw-semibold text-secondary mb-1 d-flex justify-content-between">
                                                    <span>Fondo Portada Pantalla (1920x1080)</span>
                                                    {formData.coverBackgroundUrl && <span className="text-success small"><i className="bi bi-check"></i> Subido</span>}
                                                </label>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.coverBackground}
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload('coverBackgroundUrl', e.target.files[0])}
                                                        disabled={uploadingField === 'coverBackgroundUrl'}
                                                    />
                                                </div>
                                            </div>

                                            {/* Game Background */}
                                            <div className="mb-3">
                                                <label className="form-label small fw-semibold text-secondary mb-1 d-flex justify-content-between">
                                                    <span>Fondo Juego Pantalla (1920x1080)</span>
                                                    {formData.gameBackgroundUrl && <span className="text-success small"><i className="bi bi-check"></i> Subido</span>}
                                                </label>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.gameBackground}
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload('gameBackgroundUrl', e.target.files[0])}
                                                        disabled={uploadingField === 'gameBackgroundUrl'}
                                                    />
                                                </div>
                                            </div>

                                            {/* Fan Background */}
                                            <div>
                                                <label className="form-label small fw-semibold text-secondary mb-1 d-flex justify-content-between">
                                                    <span>Fondo Celular Aficionado</span>
                                                    {formData.fanBackgroundUrl && <span className="text-success small"><i className="bi bi-check"></i> Subido</span>}
                                                </label>
                                                <div className="input-group input-group-sm">
                                                    <input
                                                        type="file"
                                                        ref={fileInputRefs.fanBackground}
                                                        className="form-control"
                                                        accept="image/*"
                                                        onChange={(e) => handleFileUpload('fanBackgroundUrl', e.target.files[0])}
                                                        disabled={uploadingField === 'fanBackgroundUrl'}
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Mini Preview Box */}
                                        <div className="card border-0 shadow-sm rounded-4 p-3 bg-dark text-white text-center">
                                            <span className="small text-muted text-uppercase fw-bold mb-2 d-block">Vista Previa Mobile Fan</span>
                                            <div
                                                className="p-3 rounded-3 mx-auto"
                                                style={{
                                                    maxWidth: '220px',
                                                    backgroundColor: formData.fanBackgroundColor || '#001A36',
                                                    border: `2px solid ${formData.fanPrimaryColor || '#E30613'}`
                                                }}
                                            >
                                                <span className="fw-bold text-white small d-block mb-2">
                                                    {formData.coverTitle || 'DETÉN EL CRONÓMETRO'}
                                                </span>
                                                <div
                                                    className="rounded py-2 px-3 fw-bold small text-white mx-auto shadow-sm"
                                                    style={{ backgroundColor: formData.fanPrimaryColor || '#E30613' }}
                                                >
                                                    DETENER
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Botones Footer */}
                                <div className="d-flex align-items-center justify-content-end gap-3 mt-4 pt-3 border-top">
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary fw-semibold px-4"
                                        onClick={() => setCurrentView('list')}
                                        disabled={isSaving || Boolean(uploadingField)}
                                    >
                                        Cancelar
                                    </button>
                                    <button
                                        type="submit"
                                        className="btn btn-danger fw-bold px-4 py-2 shadow-sm d-flex align-items-center gap-2"
                                        disabled={isSaving || Boolean(uploadingField)}
                                    >
                                        {isSaving ? (
                                            <>
                                                <div className="spinner-border spinner-border-sm" role="status"></div>
                                                <span>Guardando tema...</span>
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-check-lg fs-5"></i>
                                                <span>{formData.id ? 'Actualizar Tema' : 'Crear y Guardar Tema'}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

export default CronometerThemeModal;
